// A quiet, palette-driven title city with separate homepage crew portraits.
import { PALETTE } from './theme';
import { dk, mix } from './hd-kit';
import { paintTitleCrew } from './title-crew';
import { HD } from './render';
import { startCityBackdrop } from './start-city';

function rng(seed: number) { return () => { seed |= 0; seed = (seed + 0x6D2B79F5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

/** The title backdrop: a slow pan over the real 2x city (start-city.ts) once its first piece is painted; until then, and
 *  whenever the 2x art is off (browser tests), the palette skyline below. */
export function startBackdrop(canvas: HTMLCanvasElement): () => void {
  const stage = document.querySelector<HTMLElement>('#start .stage');
  // Measure the natural layout: transforms do not feed back into ResizeObserver sizes.
  // Short windows and an expanded room-code row must fit, rather than clip or scroll.
  const fit = () => {
    if (!stage) return;
    const scale = Math.min(1, (innerHeight - 24) / stage.offsetHeight, (innerWidth - 24) / stage.offsetWidth);
    stage.style.transform = `scale(${Math.max(.1, scale)})`;
  };
  const observer = new ResizeObserver(fit);
  if (stage) observer.observe(stage);
  addEventListener('resize', fit); fit();
  const stopFit = () => { observer.disconnect(); removeEventListener('resize', fit); };
  const crewStops = Array.from(document.querySelectorAll<HTMLCanvasElement>('#title-crew canvas'),
    (c, i) => paintTitleCrew(c, i));
  const stopCrew = () => crewStops.forEach(stop => stop());
  const stopSkyline = skyline(canvas);
  if (!HD) return () => { stopFit(); stopCrew(); stopSkyline(); };
  let stopCity = () => {}, skylineOn = true;
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches || innerWidth < 700;
  try {
    stopCity = startCityBackdrop(canvas, !still, () => {
      if (skylineOn) { skylineOn = false; stopSkyline(); }
      canvas.classList.add('city');
    });
  } catch (e) { console.warn('start page city failed, keeping the skyline', e); }
  return () => { stopFit(); stopCrew(); stopCity(); if (skylineOn) stopSkyline(); canvas.classList.remove('city'); };
}

function skyline(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d'); if (!ctx) return () => {};
  const g = ctx, W = 640, H = 360, road = 272, r = rng(2089);
  canvas.width = W; canvas.height = H;
  const base = document.createElement('canvas'); base.width = W; base.height = H;
  const b = base.getContext('2d')!;
  const rect = (x: number, y: number, w: number, h: number, c: string) => { b.fillStyle = c; b.fillRect(x, y, w, h); };
  rect(0, 0, W, H, PALETTE.midnight);
  // Layered architecture, roof equipment, lit windows and shop canopies.
  for (let layer = 0; layer < 2; layer++) {
    for (let x = -10; x < W; ) {
      const w = 30 + Math.floor(r() * 36), h = 55 + Math.floor(r() * (layer ? 150 : 190));
      const y = road - h, color = mix(PALETTE.midnight, PALETTE.slate, layer ? .3 : .14);
      rect(x, y, w, h, color); rect(x, y, w, 2, mix(color, PALETTE.slate, .5));
      rect(x + w - 5, y + 2, 5, h, dk(color, .22));
      rect(x + 7, y - 8, 13, 8, color); rect(x + 9, y - 9, 9, 2, PALETTE.slate);
      if (r() > .5) rect(x + w - 12, y - 23, 1, 23, PALETTE.slate);
      for (let wy = y + 9; wy < road - 18; wy += 11) for (let wx = x + 5; wx < x + w - 8; wx += 8) {
        if (r() > .6) rect(wx, wy, 3, 5, mix(color, PALETTE.amber, layer ? .48 : .15));
      }
      if (layer) {
        rect(x + 3, road - 17, w - 10, 15, dk(PALETTE.midnight, .2));
        rect(x + 5, road - 14, w - 14, 8, mix(color, PALETTE.amber, .32));
        rect(x + 1, road - 20, w - 6, 3, PALETTE.slate);
      }
      x += w + 4;
    }
  }
  rect(0, road, W, H - road, PALETTE.midnight);
  rect(0, road, W, 2, PALETTE.slate);
  // Architectural signs and broken reflections share the same source lights.
  for (const [x, y, label, light] of [[38, 137, 'HOTEL', PALETTE.pink], [544, 93, 'KARAOKE', PALETTE.pink], [114, 220, '24H', PALETTE.amber], [463, 218, 'RAMEN', PALETTE.amber]] as const) {
    b.font = 'bold 8px monospace'; const w = b.measureText(label).width + 8;
    rect(x - 2, y - 11, w + 4, 17, light + '12'); rect(x, y - 9, w, 13, light);
    b.fillStyle = PALETTE.midnight; b.fillText(label, x + 4, y);
    for (let i = 0; i < 12; i++) rect(x - i, road + 5 + i * 5, w + i * 2, 1 + (i % 2), light + (i < 4 ? '24' : '12'));
  }
  for (let x = 5; x < W; x += 35) rect(x, road + 51, 17, 1, PALETTE.offWhite + '18');
  const drops = Array.from({ length: 70 }, () => ({ x: r() * W, y: r() * H, v: 65 + r() * 40 }));
  function frame(dt: number) {
    g.drawImage(base, 0, 0); g.strokeStyle = PALETTE.offWhite + '22'; g.lineWidth = .5;
    g.beginPath();
    for (const d of drops) { d.y += d.v * dt; d.x -= d.v * .2 * dt; if (d.y > H) { d.y = -6; d.x = r() * (W + 60); } g.moveTo(d.x, d.y); g.lineTo(d.x - 1, d.y + 5); }
    g.stroke();
  }
  frame(0);
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.webdriver) return () => {};
  let raf = 0, last = 0, stopped = false;
  const loop = (ms: number) => {
    if (stopped) return;
    raf = requestAnimationFrame(loop);
    if (document.hidden || ms - last < 50) return;
    const dt = Math.min(.1, (ms - last) / 1000); last = ms; frame(dt);
  };
  raf = requestAnimationFrame(loop);
  return () => { stopped = true; cancelAnimationFrame(raf); };
}
